import { useEffect, useState } from "react";
import axios from "@/api/api";
import { API_ROUTES } from "@/api/routes";
import GestionUsuarios from "./GestionUsuarios";
import { toast } from "react-toastify";
import { useSession } from "@/context/SessionProvider";
import MenusSidebarManager, { AVAILABLE_MENUS } from "@/components/MenusSidebarManager";

const MANAGE_ROLES = new Set([1]); // Solo perfil 1 puede gestionar permisos

export default function PermisosUsuario() {
    const { user, loading: sessionLoading } = useSession();
    const perfilId = user?.perfil_id ?? null;
    const canManage = MANAGE_ROLES.has(Number(perfilId));

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [usuarios, setUsuarios] = useState([]);
    const [selectedUsuario, setSelectedUsuario] = useState(null);
    const [permisos, setPermisos] = useState({});
    const [searchQuery, setSearchQuery] = useState("");
    const [showGestionModal, setShowGestionModal] = useState(false);

    useEffect(() => {
        if (!sessionLoading && canManage) {
            fetchUsuarios();
        }
    }, [sessionLoading, canManage]);

    const fetchUsuarios = async () => {
        try {
            setLoading(true);
            const res = await axios.get(API_ROUTES.usuariosList);
            setUsuarios(Array.isArray(res.data) ? res.data : (res.data?.items ?? []));
        } catch (e) {
            console.error(e);
            toast.error("Error al cargar usuarios.");
        } finally {
            setLoading(false);
        }
    };

    const fetchPermisos = async (usuarioId) => {
        try {
            const res = await axios.get(API_ROUTES.usuarioDetalle(usuarioId));
            const usuario = res.data;

            // Parsear config_json o configuracion del perfil
            let config = null;
            if (usuario.configuracion) {
                config = typeof usuario.configuracion === 'string'
                    ? JSON.parse(usuario.configuracion)
                    : usuario.configuracion;
            } else if (usuario.config_json) {
                config = typeof usuario.config_json === 'string'
                    ? JSON.parse(usuario.config_json)
                    : usuario.config_json;
            }

            // Extraer los menús del sidebar
            const menusArray = config?.menus_sidebar || [];

            const permisosObj = {};
            menusArray.forEach(menuId => {
                permisosObj[menuId] = true;
            });

            setPermisos(permisosObj);
        } catch (e) {
            console.error(e);
            toast.error("Error al cargar permisos del usuario.");
        }
    };

    const handleSelectUsuario = (usuario) => {
        setSelectedUsuario(usuario);
        fetchPermisos(usuario.id);
    };

    const togglePermiso = (menuId) => {
        setPermisos(prev => ({
            ...prev,
            [menuId]: !prev[menuId]
        }));
    };

    const toggleAllMenus = (parentId) => {
        const children = AVAILABLE_MENUS.filter(m => m.parent === parentId);
        const allActive = children.every(c => permisos[c.id]);

        const nuevosPermisos = { ...permisos };
        children.forEach(child => {
            nuevosPermisos[child.id] = !allActive;
        });

        setPermisos(nuevosPermisos);
    };

    const savePermisos = async () => {
        if (!selectedUsuario) return;
        if (!selectedUsuario.perfil_id) {
            toast.error("El usuario no tiene un perfil asignado.");
            return;
        }

        try {
            setSaving(true);

            const perfilActual = await axios.get(API_ROUTES.perfilDetalle(selectedUsuario.perfil_id));
            const perfilData = perfilActual.data;

            // Parsear configuracion correctamente
            let config = perfilData.configuracion || perfilData.config_json;
            if (typeof config === 'string') {
                config = JSON.parse(config);
            }

            // Si no hay config, usar estructura por defecto
            if (!config || typeof config !== 'object') {
                config = {
                    ver_todo: false,
                    alcance: "cliente",
                    modulosVisibles: [],
                    permisos: {},
                    menus_sidebar: []
                };
            }

            // Actualizar solo menus_sidebar
            const permisosArray = Object.keys(permisos).filter(key => permisos[key]);
            config.menus_sidebar = permisosArray;

            // Guardar con nombre correcto como JSON string
            await axios.put(API_ROUTES.perfilUpdate(selectedUsuario.perfil_id), {
                configuracion: JSON.stringify(config)
            });

            toast.success("Permisos actualizados correctamente.");
            fetchUsuarios();
        } catch (e) {
            console.error(e);
            toast.error("Error al guardar permisos.");
        } finally {
            setSaving(false);
        }
    };

    // Convertir objeto de permisos a array de IDs activos
    const menusActivos = Object.keys(permisos).filter(key => permisos[key]);

    // Filtrado de usuarios usando los campos correctos del API
    const usuariosFiltrados = usuarios.filter(u =>
        u.nombre?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.usuario?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.empresa?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (!canManage) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 p-8 flex items-center justify-center">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
                    <div className="text-6xl mb-4">🔒</div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Acceso Restringido</h2>
                    <p className="text-gray-600">No tienes permisos para gestionar permisos de usuarios.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 p-4">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-xl shadow-xl p-6 mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-white">Permisos de Menús</h1>
                            <p className="text-cyan-100 mt-2">
                                Gestiona los permisos de acceso al sidebar por usuario
                            </p>
                        </div>
                        <button
                            onClick={() => setShowGestionModal(true)}
                            className="bg-white text-cyan-600 px-6 py-3 rounded-lg font-semibold hover:bg-cyan-50 transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
                        >
                            👥 Gestionar Usuarios
                        </button>
                    </div>
                </div>

                {/* Grid principal */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Lista de usuarios - 1/3 */}
                    <div className="bg-white rounded-xl shadow-md p-6">
                        <h2 className="text-xl font-bold text-gray-800 mb-4">Seleccionar Usuario</h2>

                        {/* Buscador */}
                        <div className="mb-4">
                            <input
                                type="text"
                                placeholder="Buscar usuario..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                            />
                        </div>

                        {/* Lista de usuarios */}
                        <div className="space-y-2 max-h-[600px] overflow-y-auto">
                            {loading ? (
                                <div className="text-center py-8 text-gray-500">Cargando...</div>
                            ) : usuariosFiltrados.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">No se encontraron usuarios</div>
                            ) : (
                                usuariosFiltrados.map(u => (
                                    <div
                                        key={u.id}
                                        onClick={() => handleSelectUsuario(u)}
                                        className={`p-4 rounded-lg cursor-pointer transition-all ${selectedUsuario?.id === u.id
                                            ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg'
                                            : 'bg-gray-50 hover:bg-gray-100'
                                            }`}
                                    >
                                        <div className="font-semibold">
                                            {u.nombre || 'Sin nombre'}
                                        </div>
                                        <div className={`text-sm ${selectedUsuario?.id === u.id ? 'text-cyan-100' : 'text-gray-500'}`}>
                                            {u.email || u.usuario}
                                        </div>
                                        {u.empresa && (
                                            <div className={`text-xs mt-1 ${selectedUsuario?.id === u.id ? 'text-cyan-200' : 'text-gray-400'}`}>
                                                Empresa: {u.empresa}
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Panel de permisos - 2/3 */}
                    <div className="lg:col-span-2 bg-white rounded-xl shadow-md p-6">
                        {selectedUsuario ? (
                            <>
                                {/* Header del usuario seleccionado */}
                                <div className="mb-6 pb-4 border-b border-gray-200">
                                    <h2 className="text-2xl font-bold text-gray-800">
                                        {selectedUsuario.nombre || 'Sin nombre'}
                                    </h2>
                                    <p className="text-gray-600">
                                        {selectedUsuario.email || selectedUsuario.usuario}
                                    </p>
                                    {selectedUsuario.empresa && (
                                        <p className="text-sm text-gray-500">
                                            Empresa: {selectedUsuario.empresa}
                                        </p>
                                    )}
                                </div>

                                {/* Componente de menús del sidebar */}
                                <MenusSidebarManager
                                    menusActivos={menusActivos}
                                    onToggleMenu={togglePermiso}
                                    onToggleAllMenus={toggleAllMenus}
                                    showTitle={true}
                                />

                                {/* Botón de guardar */}
                                <div className="mt-6">
                                    <button
                                        onClick={savePermisos}
                                        disabled={saving}
                                        className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 rounded-lg font-semibold hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 disabled:opacity-50"
                                    >
                                        {saving ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                                Guardando...
                                            </>
                                        ) : (
                                            <>
                                                ✓ Guardar Permisos
                                            </>
                                        )}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-20">
                                <div className="text-8xl mb-4">🔑</div>
                                <h3 className="text-2xl font-bold text-gray-800 mb-2">Selecciona un usuario</h3>
                                <p className="text-gray-600 text-center max-w-md">
                                    Elige un usuario de la lista para configurar sus permisos de acceso al sidebar
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal de Gestión de Usuarios */}
            {showGestionModal && (
                <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-6 flex items-center justify-between">
                            <h2 className="text-2xl font-bold text-white">Gestión de Usuarios</h2>
                            <button
                                onClick={() => {
                                    setShowGestionModal(false);
                                    fetchUsuarios();
                                }}
                                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            <GestionUsuarios />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}